import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import { SearchUsagePermissionsResponseDto } from '../../api/kmsapiclient'
import {
  removeUsagePermissionAsync,
  searchUsagePermissionsAsync,
} from '../../api/usagePermissionApi'
import {
  DELETE_USAGE_PERMISSION,
  GET_USAGE_PERMISSION,
} from '../../redux/actions/actionTypes'
import {
  apiCallError,
  beginApiCall,
  endApiCall,
} from '../../redux/actions/apiStatusActions'
import {
  deleteUsagePermissionSuccess,
  loadMoreUsagePermissionsSuccess,
  loadUsagePermissionsSuccess,
} from '../../redux/actions/usagePermissionsActions'
import { store, useAppDispatch } from '../../redux/configureStore'
import { IRootState } from '../../redux/reducers'
import { UsagePermissionsState } from '../../redux/reducers/initialState'
import { usePermissions } from '../common/permission/usePermissions'

import { SearchUsagePermissionsInfo, UsagePermissionsInfo } from './types'
import UsagePermissionsList from './UsagePermissionsList'

export default function UsagePermissionsPage() {
  const loading: boolean = useSelector(
    (state: IRootState) => state.apiCallsInProgress > 0,
  )
  const usagePermissions: UsagePermissionsState = useSelector(
    (state: IRootState) => state.usagePermissions,
  )

  const { checkPermissions } = usePermissions()
  const defaultSearchPermissionRequest: SearchUsagePermissionsInfo = {}

  const [searchPermissionRequest, setSearchPermissionRequest] =
    useState<SearchUsagePermissionsInfo>(defaultSearchPermissionRequest)
  const [usagePermissionsExists, setUsagePermissionsExists] = useState(true)
  const firstUpdate = useRef(true)
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false
      return
    }

    setUsagePermissionsExists(usagePermissions.usagePermissions.length > 0)

    if (
      usagePermissions.continuationToken !== undefined ||
      searchPermissionRequest?.continuationToken !== undefined
    ) {
      setSearchPermissionRequest({
        ...searchPermissionRequest,
        continuationToken: usagePermissions.continuationToken,
      })
    }

    setSearchPermissionRequest(searchPermissionRequest)
  }, [usagePermissions.usagePermissions])

  const callSearchApi = async (
    request: SearchUsagePermissionsInfo,
    successFunc,
  ) => {
    checkPermissions(store.getState(), GET_USAGE_PERMISSION)

    dispatch(beginApiCall())
    try {
      const results: SearchUsagePermissionsResponseDto =
        await searchUsagePermissionsAsync(request)

      dispatch(successFunc(results))
    } catch (error) {
      dispatch(apiCallError())
      throw error
    }
  }

  const handleDeleteKey = async (usagePermission: UsagePermissionsInfo) => {
    checkPermissions(store.getState(), DELETE_USAGE_PERMISSION)
    dispatch(beginApiCall())
    try {
      const result: string = await removeUsagePermissionAsync(
        usagePermission.id,
      )
      if (result === 'Success') {
        dispatch(deleteUsagePermissionSuccess(usagePermission))
        toast.success(t('usage_permission.delete_confirmation'))
      } else {
        dispatch(endApiCall())
        toast.error(t('usage_permission.delete_failed'))
      }
    } catch (error) {
      dispatch(apiCallError())
      throw error
    }
  }

  const handleSearch = async () => {
    await callSearchApi(searchPermissionRequest, loadUsagePermissionsSuccess)
  }

  const handleLoadMore = async () => {
    await callSearchApi(
      searchPermissionRequest,
      loadMoreUsagePermissionsSuccess,
    )
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchCriteria(event.target.name, event.target.value)
  }

  const handleBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchCriteria(event.target.name, event.target.value.trim())
  }

  const setSearchCriteria = (name, value) => {
    const newSearchPermissionRequest = {
      ...searchPermissionRequest,
      [name]: value,
    }
    setSearchPermissionRequest(newSearchPermissionRequest)
  }

  return (
    <div className="container responsive-font">
      <h3 className="page-header">{t('manage.usage_permissions')}</h3>
      <UsagePermissionsList
        usagePermissions={usagePermissions.usagePermissions}
        loading={loading}
        onSearchClick={handleSearch}
        onDeleteClick={handleDeleteKey}
        onLoadMoreClick={handleLoadMore}
        handleChange={handleChange}
        handleBlur={handleBlur}
        usagePermissionsExists={usagePermissionsExists}
        searchPermissionsRequest={searchPermissionRequest}
      />
    </div>
  )
}
